/**
 * `/flows` page — phase 32b.
 *
 * Marketing-flow observability surface. Lists every defined flow per
 * studio + per-flow aggregated counts from `member_flows` via
 * `GET /api/flows-stats`.
 *
 * Read-only at phase 32b. Operators enroll/exit members by dispatching
 * `start_member_flow` / `exit_member_flow` via CLI or REST (the /actions
 * page was retired 2026-08-04). Future iterations may
 * add per-row exit controls.
 *
 * Phase 32b ships the substrate; phase 33 (speed-to-lead welcome) +
 * phase 34 (post-booking drip) ship the first two concrete flows.
 * Until those land, this page shows a "no flows defined" empty state.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { useWhoami } from "../components/DashboardLayout";
import { generateRequestIdV7 } from "../lib/dispatch";
import { useCachedResource } from "../lib/resource-cache";
import { invalidateFlows } from "../lib/resource-cache-invalidation";
import { studioTimeZone } from "../lib/studio-tz";


interface FlowStatsEntry {
  readonly flow_name: string;
  readonly count_total: number;
  readonly count_active: number;
  readonly count_completed: number;
  readonly count_opted_out: number;
  readonly count_exited_by_trigger: number;
  readonly count_failed: number;
  readonly avg_days_to_exit: number | null;
  // comms-to-flows — immediate-transactional flows (no member_flows
  // enrolment; counts come from audit_log send rows). When `is_immediate`,
  // the member-status counts above are all 0 and these are populated.
  readonly is_immediate?: boolean;
  readonly count_fired?: number;
  readonly count_sent?: number;
  readonly count_skipped?: number;
}

interface FlowsStatsResponse {
  readonly flows: ReadonlyArray<FlowStatsEntry>;
  readonly computed_at: string;
  /**
   * AUTHORITATIVE on/off from `marketing_flow_overrides`, read per request.
   * Optional so an older server (or a test fixture) still renders.
   *
   * The catalog's `enabled` is cached up to 60 s and only cleared by a toggle
   * or save on the same dashboard process, so a toggle from the CLI or an
   * agent can lag there. (Until 2026-10-01 it was resolved once at boot and
   * stayed stale until a restart — the page showed a flow as ON while its
   * cron had been switched OFF underneath it.)
   */
  readonly enabled_by_flow?: Readonly<Record<string, boolean>>;
}

interface CatalogMarketingFlowEntry {
  readonly studio_slug: string;
  readonly name: string;
  readonly trigger_event: string;
  readonly exit_events: ReadonlyArray<string>;
  /**
   * Checkbox-toggle followup — operator-set on/off state from YAML.
   * `true` accepting new enrollments; `false` toggled off (gates
   * `start_member_flow` + cascaded existing rows to `opted_out`).
   *
   * Optional on the SPA-side type for back-compat with any cached
   * catalog responses minted before this field shipped — undefined
   * normalises to `true` (enabled).
   */
  readonly enabled?: boolean;
  /**
   * Operator-facing description of the FULL enrolment condition (YAML
   * `entry_condition`). Shown per flow so an operator sees what actually
   * enrolls a member, not just the (sometimes nominal) trigger_event.
   * `null`/absent → fall back to the raw trigger_event.
   */
  readonly entry_condition?: string | null;
  /** False for a flow whose message lives in code — its editor is view-only. */
  readonly live_editable?: boolean;
  /** The flow's stored live edit (migration 204); null = runs as shipped. */
  readonly live_edit?: {
    readonly updated_at: string;
    readonly shipped_changed: boolean;
    readonly not_running_reasons: ReadonlyArray<string>;
  } | null;
  /** The loop a run resolves (2026-10-01); null/absent = no loop. */
  readonly repeat?: {
    readonly last_steps: number;
    readonly times: number | "infinite";
    readonly send_time: string | null;
    readonly cycle_hours: number;
    /** Articles the loop rotates through, one per repeat (0 = none). */
    readonly article_count?: number;
  } | null;
  /**
   * When the flow asks, in words, for a flow whose schedule is set on
   * `/config` (2026-10-21: only `session_feedback_request`, "after session 1,
   * then every 5 sessions (sessions 1, 6, 11, 16 …)"). null/absent = none.
   */
  readonly cadence_summary?: string | null;
}

/** "loops every 30 days · 4 articles" — the `/flows` badge for a flow's loop. */
function loopBadgeText(
  repeat: NonNullable<CatalogMarketingFlowEntry["repeat"]>,
): string {
  const cycleHours = repeat.cycle_hours;
  const every =
    cycleHours % 24 === 0
      ? `loops every ${String(cycleHours / 24)} ${cycleHours === 24 ? "day" : "days"}`
      : `loops every ${String(cycleHours)} hours`;
  const articles = repeat.article_count ?? 0;
  return articles > 0
    ? `${every} · ${String(articles)} ${articles === 1 ? "article" : "articles"}`
    : every;
}

/** The loop in a sentence, for the badge's tooltip. */
function loopBadgeTitle(
  repeat: NonNullable<CatalogMarketingFlowEntry["repeat"]>,
): string {
  const steps =
    repeat.last_steps === 1
      ? "The last step repeats"
      : `The last ${String(repeat.last_steps)} steps repeat`;
  const how =
    repeat.times === "infinite"
      ? "until the member leaves the flow"
      : `${String(repeat.times)} times in all, counting the first`;
  const at = repeat.send_time !== null ? `, sent at ${repeat.send_time} studio time` : "";
  const articles = repeat.article_count ?? 0;
  const reading =
    articles > 0
      ? ` Each repeat features the next of ${String(articles)} ${articles === 1 ? "article" : "articles"}, then round again.`
      : "";
  return `${steps} ${how}${at}.${reading} Each repeat uses the version of the flow saved when it starts.`;
}

interface CatalogResponse {
  readonly marketing_flows: ReadonlyArray<CatalogMarketingFlowEntry>;
}

// comms-to-flows — the four send buckets an immediate-transactional flow's
// counts split into. Drives the Transactional-flows table columns + the
// drill-down popup.
type ImmediateBucket = "fired" | "sent" | "skipped" | "failed";

const BUCKET_LABEL: Record<ImmediateBucket, string> = {
  fired: "Fired",
  sent: "Sent",
  skipped: "Skipped",
  failed: "Failed",
};

/** The Member-flows table's own buckets (enrolment statuses). */
const DRIP_BUCKET_LABEL: Record<string, string | undefined> = {
  total: "Total",
  active: "Active",
  completed: "Completed",
  opted_out: "Opted out",
  // Matches the column header. It drives three things — the header, each
  // phone card's row label, and the drill-down popup title — and they should
  // agree; the popup's `<code>flow</code> · Exited` reads fine without the
  // longer spelling, which the header tooltip still carries.
  exited_by_trigger: "Exited",
  failed: "Failed",
};


/**
 * The column model BOTH flow tables lay themselves out on: `On`, `Flow`, then
 * seven equal count columns.
 *
 * The two tables stack on one page and describe the same objects, so columns
 * that don't line up read as an inconsistency rather than as two independent
 * tables — and the eye tries to compare figures that happen to be vertically
 * adjacent. `table-layout: fixed` plus one shared `<colgroup>` makes the widths
 * a property of the PAGE rather than of each table's own content, which is what
 * keeps them aligned as the data changes: without it, one long flow name in
 * either table silently shifts every column to its right.
 *
 * Transactional flows carry four counts, not seven, so that table renders three
 * blank cells after `Flow` — its four land under the member table's last four.
 */
function FlowsColGroup(): JSX.Element {
  return (
    <colgroup>
      <col className="flows-col-on" />
      <col className="flows-col-name" />
      {Array.from({ length: 7 }, (_, i) => (
        <col key={i} className="flows-col-count" />
      ))}
    </colgroup>
  );
}

interface FlowMemberRow {
  readonly subject_id: string;
  readonly member_id: string | null;
  readonly display_name: string | null;
  readonly email: string | null;
  readonly outcome: string;
  readonly skipped: boolean;
  /** Vendor REFUSED the message after a successful dispatch (WhatsApp leg). */
  readonly delivery_failed: boolean;
  /** Refused, then re-issued as the fallback template and DELIVERED. */
  readonly recovered: boolean;
  readonly occurred_at: string;
}

interface FlowMembersResponse {
  readonly flow_name: string;
  readonly bucket: ImmediateBucket;
  readonly members: ReadonlyArray<FlowMemberRow>;
}

/**
 * Narrow the process-wide catalog flow list to ONE studio.
 *
 * `/api/catalog` lists the flows of every studio the dashboard can load flow
 * YAML for, and is served from a single shared cache (60 s) — it is not, and
 * cannot be, scoped per request. `/flows` is a single-studio page and
 * `handleFlowToggle` writes against `(name, studio_slug)`, so the page must
 * narrow the same way or it pairs a stats row with a foreign studio's entry.
 *
 * `studioSlug === null` means whoami has not resolved yet; pass everything
 * through rather than blanking the table for a frame.
 */
export function scopeFlowsToStudio<T extends { readonly studio_slug: string }>(
  flows: ReadonlyArray<T>,
  studioSlug: string | null,
): ReadonlyArray<T> {
  if (studioSlug === null) return flows;
  return flows.filter((f) => f.studio_slug === studioSlug);
}

/**
 * Resolve what the ON checkbox should say for one flow.
 *
 * `enabled_by_flow` is the authoritative per-request read of
 * `marketing_flow_overrides`. A flow with NO row is ABSENT from that map
 * rather than `false` — absence means "no operator decision recorded", and
 * the gates do not agree on what to do with it: `start_member_flow`'s
 * resolver defaults a missing row to ON, while the cron enrollers (winback,
 * expiry-alert, post-session capture, session-feedback) ship dark and fire
 * ONLY on an explicit `enabled = true` row. One boolean cannot be right for
 * both, so `noDecision` is reported alongside `enabled` and the page renders
 * the ambiguity instead of resolving it silently.
 *
 * `noDecision` is false while `enabledByFlow` itself is undefined — that is
 * a stats response that predates the field, not a missing row.
 */
export function resolveFlowToggleState(
  flowName: string,
  enabledByFlow: Readonly<Record<string, boolean>> | undefined,
  catalogEntry: { readonly enabled?: boolean } | undefined,
): { readonly enabled: boolean; readonly noDecision: boolean } {
  const liveEnabled = enabledByFlow?.[flowName];
  const enabled =
    liveEnabled !== undefined
      ? liveEnabled
      : catalogEntry === undefined
        ? true
        : catalogEntry.enabled !== false;
  return {
    enabled,
    noDecision: enabledByFlow !== undefined && liveEnabled === undefined,
  };
}

export function Flows(): JSX.Element {
  const whoami = useWhoami();
  const [data, setData] = useState<FlowsStatsResponse | null>(null);
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Phase 41 followup — fast-forward toggle state. `null` while
  // loading; otherwise the resolved current value (override/env/default).
  const [fastForward, setFastForward] = useState<{
    enabled: boolean;
    source: "db" | "default";
    receiver_pid: number | null;
  } | null>(null);
  const [fastForwardError, setFastForwardError] = useState<string | null>(null);
  const [fastForwardSaving, setFastForwardSaving] = useState(false);
  // Checkbox-toggle followup — per-flow on/off state. Optimistic
  // update on click; the catalog refetch on success backfills the
  // authoritative truth.
  const [pendingToggle, setPendingToggle] = useState<string | null>(null);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const [toggleSuccess, setToggleSuccess] = useState<string | null>(null);
  // comms-to-flows drill-down — which (flow, bucket) count was clicked in
  // the Transactional-flows table. Opens the members popup. null = closed.
  // `bucket` is a plain string, not `ImmediateBucket`: the Member-flows table
  // drills on enrolment statuses (total / active / completed / opted_out /
  // exited_by_trigger / failed), which are a different vocabulary from the
  // transactional send buckets. The server validates whichever it gets.
  const [drill, setDrill] = useState<{
    flow: string;
    bucket: string;
  } | null>(null);

  // Phase 67 — stats + catalog are cache-backed (keys `flows-stats` +
  // `catalog`), so returning to `/flows` renders the last-seen table
  // instantly with no spinner, then silently revalidates. The local
  // `data`/`catalog` state is seeded from the cache values below so the
  // optimistic per-flow toggle (which mutates `catalog` in place) keeps
  // working unchanged.
  const { data: statsCache, error: statsErr } =
    useCachedResource<FlowsStatsResponse>("flows-stats", () =>
      api.get<FlowsStatsResponse>("/api/flows-stats"),
    );
  const { data: catalogCache, error: catalogErr } =
    useCachedResource<CatalogResponse>("catalog", () =>
      api.get<CatalogResponse>("/api/catalog"),
    );
  // Seed/refresh local state from the cache. The toggle handler mutates
  // local state directly for the optimistic flip; a fresh cache value
  // (e.g. after `invalidateFlows()`) replaces it here.
  useEffect(() => {
    if (statsCache !== undefined) setData(statsCache);
  }, [statsCache]);
  useEffect(() => {
    if (catalogCache !== undefined) setCatalog(catalogCache);
  }, [catalogCache]);
  useEffect(() => {
    const msg = statsErr?.message ?? catalogErr?.message ?? null;
    setError(msg);
    // Loading clears once either resource has resolved (or errored).
    if (statsCache !== undefined || catalogCache !== undefined || msg !== null) {
      setLoading(false);
    }
  }, [statsErr, catalogErr, statsCache, catalogCache]);

  // Best-effort fast-forward state — 404 in production. Treated as
  // "feature unavailable" (toggle hidden); doesn't fail the page. Dev-
  // only endpoint, not cached.
  useEffect(() => {
    let cancelled = false;
    api
      .devFastForwardGet()
      .then((ffValue) => {
        if (cancelled) return;
        setFastForward({
          enabled: ffValue.enabled,
          source: ffValue.source,
          receiver_pid: ffValue.receiver_pid,
        });
      })
      .catch(() => {
        /* feature unavailable in prod — leave toggle hidden */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleFlowEnabled = async (
    flowName: string,
    currentEnabled: boolean,
  ): Promise<void> => {
    if (pendingToggle !== null) return;
    const next = !currentEnabled;
    setPendingToggle(flowName);
    setToggleError(null);
    setToggleSuccess(null);
    // Optimistic update on the in-memory catalog so the checkbox flips
    // immediately. The refetch below replaces this with the authoritative
    // server response.
    setCatalog((prev) =>
      prev === null
        ? prev
        : {
            ...prev,
            marketing_flows: prev.marketing_flows.map((f) =>
              f.name === flowName ? { ...f, enabled: next } : f,
            ),
          },
    );
    try {
      const resp = await api.setFlowEnabled(flowName, {
        enabled: next,
        request_id: generateRequestIdV7(),
      });
      const serverEnabled = resp.response.enabled;
      const exited = resp.response.members_exited;
      setToggleSuccess(
        serverEnabled
          ? `'${flowName}' is now ON. New members can enroll again.`
          : exited > 0
            ? `'${flowName}' is now OFF. ${exited} active member${exited === 1 ? "" : "s"} exited (status='opted_out').`
            : `'${flowName}' is now OFF. No active members to exit.`,
      );
      // Pin the catalog to the action response's authoritative `enabled`
      // value FIRST — the backend clears its catalog cache on success, but
      // if that lags (e.g. another dashboard process served the read) we
      // want the UI to reflect what actually got written. Then refetch stats
      // (always reflects DB truth) + catalog (rebuilt from the database).
      setCatalog((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              marketing_flows: prev.marketing_flows.map((f) =>
                f.name === flowName ? { ...f, enabled: serverEnabled } : f,
              ),
            },
      );
      // Phase 67 — bust the shared cache so the stats + catalog hooks
      // revalidate. We still re-pin the toggled flow's `enabled` to the
      // action response locally so a lagging cache snapshot can never
      // overwrite the just-written truth.
      invalidateFlows();
      const [statsValue, catalogValue] = await Promise.all([
        api.get<FlowsStatsResponse>("/api/flows-stats"),
        api.get<CatalogResponse>("/api/catalog"),
      ]);
      setData(statsValue);
      // Merge: keep the catalog's other fields fresh, but re-pin the
      // toggled flow's `enabled` to the action response so we never
      // overwrite truth with stale cache state.
      setCatalog({
        ...catalogValue,
        marketing_flows: catalogValue.marketing_flows.map((f) =>
          f.name === flowName ? { ...f, enabled: serverEnabled } : f,
        ),
      });
    } catch (e) {
      // Revert the optimistic update on failure.
      setCatalog((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              marketing_flows: prev.marketing_flows.map((f) =>
                f.name === flowName ? { ...f, enabled: currentEnabled } : f,
              ),
            },
      );
      const msg =
        e instanceof ApiError
          ? `${e.code ?? "api_error"}: ${e.message}`
          : (e as Error).message;
      setToggleError(`Toggle for '${flowName}' failed — ${msg}`);
    } finally {
      setPendingToggle(null);
    }
  };

  const toggleFastForward = async (): Promise<void> => {
    if (fastForward === null || fastForwardSaving) return;
    const next = !fastForward.enabled;
    setFastForwardSaving(true);
    setFastForwardError(null);
    try {
      const resp = await api.devFastForwardSet(next);
      setFastForward({
        enabled: resp.enabled,
        source: "db",
        receiver_pid: fastForward.receiver_pid,
      });
      if (!resp.signaled) {
        // Warn but don't error — the flag file is written; receiver
        // will pick it up on next start.
        setFastForwardError(
          `Saved to flags file but receiver not signaled${resp.signal_error ? `: ${resp.signal_error}` : ""}. Restart the receiver to pick up.`,
        );
      }
    } catch (e) {
      setFastForwardError((e as Error).message);
    } finally {
      setFastForwardSaving(false);
    }
  };

  // E-Pulsive is the only studio shipping flow YAML today, so scoped and
  // unscoped agree in practice — this keeps them agreeing when a second
  // vertical lands. See `scopeFlowsToStudio`.
  const studioSlug = whoami.data?.studio?.slug ?? null;
  const studioFlows = useMemo(
    () =>
      catalog === null
        ? []
        : scopeFlowsToStudio(catalog.marketing_flows, studioSlug),
    [catalog, studioSlug],
  );

  // Phase 41 followup — always render the stats table, even when no
  // members are enrolled. Build a unified row list: every defined
  // catalog flow gets a row; stats from member_flows fill counts
  // where available, otherwise zeros.
  const tableRows: ReadonlyArray<FlowStatsEntry> = (() => {
    const statsByName = new Map<string, FlowStatsEntry>();
    if (data !== null) {
      for (const f of data.flows) statsByName.set(f.flow_name, f);
    }
    const defined: FlowStatsEntry[] = [];
    if (catalog !== null) {
      for (const c of studioFlows) {
        const existing = statsByName.get(c.name);
        defined.push(
          existing ?? {
            flow_name: c.name,
            count_total: 0,
            count_active: 0,
            count_completed: 0,
            count_opted_out: 0,
            count_exited_by_trigger: 0,
            count_failed: 0,
            avg_days_to_exit: null,
          },
        );
        statsByName.delete(c.name);
      }
    }
    // Any stats rows without a matching catalog entry (e.g. a flow was
    // removed from YAML but member_flows still has historical rows) —
    // append at the end so operators can still see them.
    for (const orphan of statsByName.values()) defined.push(orphan);
    return defined;
  })();

  // comms-to-flows — split the unified list: immediate-transactional flows
  // (fire-on-event-and-send; stats from audit_log) render in their own
  // "Transactional flows" table with Fired/Sent/Skipped/Failed columns; the
  // classic drip flows keep the member_flows enrolment columns below.
  const memberRows = tableRows.filter((f) => f.is_immediate !== true);
  const immediateRows = tableRows.filter((f) => f.is_immediate === true);


  // comms-to-flows — shared leading cells (On toggle + Flow name / Edit /
  // Enters-when) for a flow row, reused by the member-flows table AND the
  // transactional-flows table so the two stay visually consistent.
  const renderFlowLeadCells = (f: FlowStatsEntry): JSX.Element => {
    const catalogEntry = studioFlows.find((c) => c.name === f.flow_name);
    // LIVE override row wins over the catalog's boot-time snapshot; absence
    // is surfaced rather than guessed. See `resolveFlowToggleState`.
    const { enabled, noDecision } = resolveFlowToggleState(
      f.flow_name,
      data?.enabled_by_flow,
      catalogEntry,
    );
    const isOrphan = catalogEntry === undefined;
    const saving = pendingToggle === f.flow_name;
    const entryCondition = catalogEntry?.entry_condition ?? null;
    const triggerEvent = catalogEntry?.trigger_event ?? null;
    return (
      <>
        <td className="flows-cell-on" style={{ textAlign: "center" }}>
          {isOrphan ? (
            <span className="subtle" title="No YAML — toggle not available">
              —
            </span>
          ) : (
            <input
              type="checkbox"
              checked={enabled}
              disabled={saving || pendingToggle !== null}
              onChange={() => {
                // Member flows: confirm OFF when active members would be
                // cascaded to opted_out. Immediate flows have no enrolment
                // (count_active is always 0), so this is a plain flip.
                if (enabled && f.count_active > 0) {
                  const ok = window.confirm(
                    `Toggle '${f.flow_name}' OFF?\n\n${f.count_active} active member${f.count_active === 1 ? "" : "s"} will exit immediately (status='opted_out'). This is irreversible — they would need to be re-enrolled manually.`,
                  );
                  if (!ok) return;
                }
                void toggleFlowEnabled(f.flow_name, enabled);
              }}
              title={
                noDecision
                  ? "No operator decision recorded — this box shows the YAML default, which is NOT what every gate does with a missing row. Click to record one."
                  : enabled
                    ? "ON — accepting new enrollments"
                    : "OFF — start_member_flow rejects with flow_disabled"
              }
            />
          )}
        </td>
        <td className="flows-cell-name">
          <code>{f.flow_name}</code>
          {" · "}
          <Link to={`/flows/${f.flow_name}/edit`}>
            {catalogEntry?.live_editable === false ? "View" : "Edit"}
          </Link>
          {!enabled && !isOrphan && !noDecision && (
            <>
              {" · "}
              {/* `.tag-error`, not `.tag-danger` — the latter is defined ONLY
                  under `.page-studio-config`, so this badge has been rendering
                  as a plain grey `.tag` here rather than red. Grey is now what
                  "not set" means, so the two states have to differ. */}
              <span className="tag tag-error">OFF</span>
            </>
          )}
          {noDecision && !isOrphan && (
            <>
              {" · "}
              <span
                className="tag"
                title="marketing_flow_overrides has no row for this flow. The checkbox shows the YAML default; the cron enrollers would treat the missing row as OFF regardless. Toggling once records a row and removes the ambiguity."
              >
                not set
              </span>
            </>
          )}
          {catalogEntry?.repeat != null && (
            <>
              {" · "}
              <span className="tag" title={loopBadgeTitle(catalogEntry.repeat)}>
                {loopBadgeText(catalogEntry.repeat)}
              </span>
            </>
          )}
          {catalogEntry?.live_edit != null && (
            <>
              {" · "}
              <span
                className="tag"
                title="Changed in the flow editor: members who enter after the save get the edited steps instead of the version in git."
              >
                edited live
              </span>
              {catalogEntry.live_edit.shipped_changed && (
                <>
                  {" · "}
                  <span
                    className="tag tag-warn"
                    title="A deploy changed this flow's file after the live edit was saved. The live edit still runs; Restore shipped version in the editor uses the new file."
                  >
                    shipped version changed
                  </span>
                </>
              )}
              {catalogEntry.live_edit.not_running_reasons.length > 0 && (
                <>
                  {" · "}
                  <span
                    className="tag tag-error"
                    title={`The shipped version runs instead: ${catalogEntry.live_edit.not_running_reasons.join("; ")}`}
                  >
                    edit not running
                  </span>
                </>
              )}
            </>
          )}
          {(entryCondition !== null || triggerEvent !== null) && (
            <div
              className="subtle"
              style={{
                marginTop: 4,
                maxWidth: 460,
                fontWeight: 400,
                lineHeight: 1.4,
                whiteSpace: "normal",
              }}
              title="Full enrolment condition — what actually causes a member to enter this flow (beyond the trigger event name)."
            >
              <strong style={{ fontWeight: 600 }}>Enters when:</strong>{" "}
              {entryCondition ?? `${triggerEvent} event fires`}
            </div>
          )}
          {catalogEntry?.cadence_summary != null && (
            <div
              className="subtle"
              data-testid={`flow-cadence-${f.flow_name}`}
              style={{
                marginTop: 4,
                maxWidth: 460,
                fontWeight: 400,
                lineHeight: 1.4,
                whiteSpace: "normal",
              }}
              title="Which sessions a member is asked about. Set on Config → Marketing flows."
            >
              <strong style={{ fontWeight: 600 }}>Asks:</strong>{" "}
              {catalogEntry.cadence_summary}
              {" · "}
              <Link
                to={`/config/${encodeURIComponent(catalogEntry.studio_slug)}?section=marketing-flows`}
              >
                change on Config
              </Link>
            </div>
          )}
        </td>
      </>
    );
  };

  // A clickable count. EVERY count on this page is now one — both tables,
  // every bucket, zero included.
  //
  // Zeros used to render as dead black text (the cell gated on `value > 0`),
  // which is indistinguishable from a number that simply doesn't work — Mario
  // read it exactly that way. "Nobody is in this bucket" is a real answer and
  // the popup gives it; the ambiguity was the bug.
  const countButton = (
    flowName: string,
    bucket: string,
    value: number,
    label: string,
  ): JSX.Element => (
    // `data-label` is what the phone layout reads: below 768px the tables
    // become one card per flow and each cell prints its own column name, so
    // the header row (which has nowhere to go at 390px) isn't needed.
    <td data-label={label}>
      <button
        type="button"
        onClick={() => setDrill({ flow: flowName, bucket })}
        title={`Show the members behind ${label} = ${value}`}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          color: "var(--c-signal-text-53)",
          cursor: "pointer",
          textDecoration: "underline",
          font: "inherit",
        }}
      >
        {value}
      </button>
    </td>
  );

  const renderCountCell = (
    f: FlowStatsEntry,
    bucket: ImmediateBucket,
    value: number,
  ): JSX.Element =>
    countButton(f.flow_name, bucket, value, BUCKET_LABEL[bucket]);

  /** A drip (member-flow) count — drills to the `member_flows` enrolments. */
  const renderDripCountCell = (
    f: FlowStatsEntry,
    bucket: string,
    value: number,
  ): JSX.Element =>
    countButton(f.flow_name, bucket, value, DRIP_BUCKET_LABEL[bucket] ?? bucket);

  return (
    <div className="page-flows">
      {/* The cron automations moved to `/automations`, so the title is just
          the page again. The blurbs that stood under both headings described
          what the tables already show — a column called "Total" from
          `member_flows` does not need a sentence saying so. */}
      <h1 className="page-title">Flows</h1>

      <h2 style={{ marginTop: 24 }}>Member flows</h2>

      {/* Fast-forward toggle (phase 41; rewired 2026-06-09 to a live
          DB-backed flag that WORKS IN PRODUCTION). Collapses every
          `step.sleepUntil` in marketing-flow sagas to ~now+1s so the
          T-24h reminder / T-2h nudge fire seconds after enrolment instead
          of on the wall-clock. Writes studio_config.marketing_flow_fast_forward;
          the receiver polls it (~15s) and honors it even in prod. */}
      {fastForward !== null && (
        <div
          className="panel"
          style={{
            marginBottom: 16,
            padding: 12,
            border: "1px solid var(--c-line-85)",
            borderRadius: 4,
            background: "var(--c-surface-97)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontWeight: 500,
                cursor: fastForwardSaving ? "wait" : "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={fastForward.enabled}
                onChange={toggleFastForward}
                disabled={fastForwardSaving}
              />
              Fast-forward marketing flows
            </label>
            <span className="subtle">
              {fastForward.enabled
                ? "ON — `step.sleepUntil` collapses to ~1s. Reminders fire seconds after enrolment (allow ~15s after toggling for the receiver to pick it up)."
                : "OFF — sagas wake on the real wall-clock schedule."}
            </span>
          </div>
          <p className="subtle" style={{ marginTop: 6, marginBottom: 0 }}>
            Source:{" "}
            <code>
              {fastForward.source === "db"
                ? "studio_config (live DB override)"
                : "default (off)"}
            </code>{" "}
            · applies in production · ~15s to propagate to the receiver.
          </p>
          {fastForwardError !== null && (
            <p className="tag tag-danger" style={{ marginTop: 6 }}>
              {fastForwardError}
            </p>
          )}
        </div>
      )}

      {loading && <div>Loading…</div>}
      {error !== null && (
        <div className="tag tag-danger">Error: {error}</div>
      )}

      {/* Checkbox-toggle followup — per-row toggle feedback panel. */}
      {(toggleError !== null || toggleSuccess !== null) && (
        <div style={{ marginBottom: 12 }}>
          {toggleError !== null && (
            <div className="tag tag-danger" style={{ display: "inline-block" }}>
              {toggleError}
            </div>
          )}
          {toggleSuccess !== null && (
            <div
              className="tag"
              style={{
                display: "inline-block",
                background: "var(--c-ok-wash-92)",
                color: "var(--c-ok-text-31)",
                padding: "4px 8px",
                borderRadius: 4,
              }}
            >
              {toggleSuccess}
            </div>
          )}
        </div>
      )}

      {/* Phase 41 followup — always render the table, even when no
          member is enrolled. Empty defined-but-not-yet-run flows
          show with zero counts. */}
      {!loading && (
        <table className="data-table flows-table table-cards">
          <FlowsColGroup />
          <thead>
            <tr>
              <th title="Toggle ON to accept new enrollments. Toggle OFF gates start_member_flow + exits every active member of this flow.">
                On
              </th>
              <th>Flow</th>
              <th>Total</th>
              <th>Active</th>
              <th>Completed</th>
              <th>Opted out</th>
              {/* Short labels, full meaning on hover — the long spellings
                  wrapped to three lines in a 104px column and were the only
                  headers doing so. */}
              <th title="Left the flow early because one of its exit_events fired.">
                Exited
              </th>
              <th>Failed</th>
              <th title="Mean days from enrolment to leaving the flow.">
                Average days
              </th>
            </tr>
          </thead>
          <tbody>
            {memberRows.length === 0 ? (
              <tr>
                <td colSpan={9} className="subtle" style={{ textAlign: "center", padding: 24 }}>
                  No drip flows defined. Per-studio flow YAMLs live under{" "}
                  <code>verticals/&lt;v&gt;/studios/&lt;slug&gt;/flows/&lt;name&gt;.yaml</code>.
                </td>
              </tr>
            ) : (
              memberRows.map((f) => (
                <tr key={f.flow_name}>
                  {renderFlowLeadCells(f)}
                  {/* 2026-07-29 — drip counts drill down too. Each bucket
                      lists the `member_flows` enrolments behind it. */}
                  {renderDripCountCell(f, "total", f.count_total)}
                  {renderDripCountCell(f, "active", f.count_active)}
                  {renderDripCountCell(f, "completed", f.count_completed)}
                  {renderDripCountCell(f, "opted_out", f.count_opted_out)}
                  {renderDripCountCell(
                    f,
                    "exited_by_trigger",
                    f.count_exited_by_trigger,
                  )}
                  {renderDripCountCell(f, "failed", f.count_failed)}
                  <td data-label="Average days">
                    {f.avg_days_to_exit === null
                      ? "—"
                      : f.avg_days_to_exit.toFixed(1)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {/* comms-to-flows — Transactional (immediate) flows. No member_flows
          enrolment; counts are live sends from audit_log. The booking flows'
          counts drill down to the members behind them. */}
      {!loading && (
        <section style={{ marginTop: 32 }} aria-label="Transactional flows">
          <h2>Transactional flows</h2>
          <table className="data-table flows-table table-cards">
            <FlowsColGroup />
            <thead>
              <tr>
                <th title="Toggle ON/OFF whether this flow sends.">On</th>
                <th>Flow</th>
                {/* Three blanks so Fired/Sent/Skipped/Failed land under the
                    member table's LAST four count columns rather than under
                    Total/Active/Completed, which measure a different thing. */}
                <th aria-hidden="true" />
                <th aria-hidden="true" />
                <th aria-hidden="true" />
                <th title="Send attempts, by email or WhatsApp: an event that sends both counts two, and a send retried after a failure counts each try.">
                  Fired
                </th>
                <th title="Send attempts that went out, including a WhatsApp refusal re-sent as its fallback template.">
                  Sent
                </th>
                <th title="Emails skipped because the address is a placeholder. A WhatsApp step with no number to send to is not counted anywhere.">
                  Skipped
                </th>
                <th title="Send attempts that errored, or that WhatsApp refused with no fallback re-send.">Failed</th>
              </tr>
            </thead>
            <tbody>
              {immediateRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="subtle"
                    style={{ textAlign: "center", padding: 24 }}
                  >
                    No transactional flows defined.
                  </td>
                </tr>
              ) : (
                immediateRows.map((f) => {
                  return (
                    <tr key={f.flow_name}>
                      {renderFlowLeadCells(f)}
                      <td />
                      <td />
                      <td />
                      {renderCountCell(f, "fired", f.count_fired ?? 0)}
                      {renderCountCell(f, "sent", f.count_sent ?? 0)}
                      {renderCountCell(f, "skipped", f.count_skipped ?? 0)}
                      {renderCountCell(f, "failed", f.count_failed)}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </section>
      )}

      {/* comms-to-flows — drill-down popup: the members behind a clicked
          Transactional-flows count. */}
      {drill !== null && (
        <FlowMembersModal
          flow={drill.flow}
          bucket={drill.bucket}
          onClose={() => setDrill(null)}
        />
      )}


      {/* drill-down popup rendered at the page root above. */}
    </div>
  );
}

/**
 * comms-to-flows — the members-behind-a-count popup. Fetches
 * `/api/flow-members?flow=&bucket=` (the sends in that bucket, resolved to
 * their member via the booking subject) and lists them. Read-only.
 */
function FlowMembersModal({
  flow,
  bucket,
  onClose,
}: {
  readonly flow: string;
  readonly bucket: string;
  readonly onClose: () => void;
}): JSX.Element {
  const [data, setData] = useState<FlowMembersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<FlowMembersResponse>(
        `/api/flow-members?flow=${encodeURIComponent(flow)}&bucket=${encodeURIComponent(bucket)}`,
      )
      .then((resp) => {
        if (cancelled) return;
        setData(resp);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError
            ? `${e.code ?? "api_error"}: ${e.message}`
            : (e as Error).message,
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [flow, bucket]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--c-scrim-40-k)",
        display: "flex",
        // Centred both ways (2026-07-29). It was `flex-start`, which pinned the
        // card to the top of the viewport — on a tall screen with a short
        // member list that reads as a dropdown hanging off the header rather
        // than a dialog. `overflowY: auto` on the overlay keeps a card taller
        // than the viewport reachable, which is the thing top-alignment was
        // implicitly buying.
        alignItems: "center",
        justifyContent: "center",
        overflowY: "auto",
        zIndex: 1000,
        padding: 40,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--c-surface)",
          borderRadius: 8,
          maxWidth: 680,
          width: "100%",
          // Fits inside the overlay's 40px padding rather than overlapping it.
          maxHeight: "calc(100vh - 80px)",
          overflowY: "auto",
          margin: "auto",
          padding: 20,
          boxShadow: "var(--c-modal-shadow-flow)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ margin: 0 }}>
            <code>{flow}</code> ·{" "}
            {BUCKET_LABEL[bucket as ImmediateBucket] ??
              DRIP_BUCKET_LABEL[bucket] ??
              bucket}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: 22,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
        <p className="subtle" style={{ marginTop: 4 }}>
          Members behind this count — most recent first.
        </p>
        {loading && <div>Loading…</div>}
        {error !== null && <div className="tag tag-danger">Error: {error}</div>}
        {data !== null && data.members.length === 0 && (
          <p className="subtle">No members in this bucket.</p>
        )}
        {data !== null && data.members.length > 0 && (
          <table className="data-table table-cards">
            <thead>
              <tr>
                <th>Member</th>
                <th>Email</th>
                <th>When</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m, i) => (
                <tr key={`${m.subject_id}:${i}`}>
                  <td className="table-card-title">
                    {m.member_id !== null ? (
                      <Link to={`/members/${m.member_id}`}>
                        {m.display_name ?? "(no name)"}
                      </Link>
                    ) : (
                      <span
                        className="subtle"
                        title={`${m.subject_id !== "" ? `subject ${m.subject_id}: ` : ""}no member matches this send's subject or its recipient (e.g. a test send with a synthetic id)`}
                      >
                        (unresolved)
                      </span>
                    )}
                  </td>
                  <td data-label="Email">{m.email ?? "—"}</td>
                  <td data-label="When" className="subtle">
                    {new Date(m.occurred_at).toLocaleString("en-GB", {
                      timeZone: studioTimeZone(),
                    })}
                  </td>
                  <td data-label="Result">
                    {m.skipped ? (
                      <span
                        className="tag"
                        title="No real email — placeholder recipient"
                      >
                        skipped
                      </span>
                    ) : m.delivery_failed ? (
                      // Dispatch succeeded, the vendor then refused it — the
                      // commonest cause is Meta error 131047, a free-form
                      // WhatsApp sent outside the 24-hour reply window. Reading
                      // this as "sent" is what made a dead channel look healthy.
                      <span
                        className="tag tag-danger"
                        title="Clavis sent it, the vendor rejected it — see /data/outbound_messages for the vendor error code"
                      >
                        undelivered
                      </span>
                    ) : m.recovered ? (
                      // The free-form send was refused and the delivery-failure
                      // alerter re-issued it as the approved fallback template,
                      // successfully. The member DID get it — so not a failure —
                      // but worth showing, because the free-form path is being
                      // refused and only the fallback is carrying the message.
                      <span
                        className="tag tag-success"
                        title="Refused as free-form, re-sent as the approved template and delivered"
                      >
                        sent · via fallback
                      </span>
                    ) : m.outcome === "ok" ? (
                      <span className="tag tag-success">sent</span>
                    ) : (
                      <span className="tag tag-danger">{m.outcome}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
